<script setup lang="ts">
export interface CardSubscriptionNewProps {
  title: string;
  description?: string;
  price: string;
  active?: boolean;
  disabled?: boolean;
  buttonLabel?: string;
  durationLabel?: string;
  releaseDateTitle?: string;
  accessLabels?: IModelAccessLabels;
  allowedAccessIdentityItems?: IModelCommerceIdentityItems[];
  allowedAccessIdentityItemsClass?: string;
  isRegionAvailable?: boolean;
  regionUnavailableMessage?: string;
}

const { t } = useI18n();
const props = withDefaults(defineProps<CardSubscriptionNewProps>(), {
  allowedAccessIdentityItemsClass: "bg-[var(--base-subscriptionCard--bgColor)]",
  isRegionAvailable: true,
});
const emit = defineEmits(["clickSubscribe"]);

const onClickSubscribe = () => emit("clickSubscribe");
</script>

<template>
  <div class="cardSubscriptionNew">
    <div class="font-bold text-xs sm:text-base text-main mb-4">
      {{ props.title }}
    </div>
    <div v-if="props.description" class="cardSubscriptionNew__description text-secondary">
      {{ props.description }}
    </div>

    <div class="flex justify-between text-secondary text-xs mb-4">
      <div v-if="props.accessLabels" class="flex items-center gap-2">
        {{ props.accessLabels.title }}
        <BaseButton v-tooltip="props.accessLabels.description" :name="props.accessLabels.description" tag="div" type="icon">
          <IconInfo />
        </BaseButton>
      </div>
      <div v-if="props.durationLabel" class="flex items-center">
        {{ props.durationLabel }}
      </div>
    </div>

    <ul
      v-if="props.allowedAccessIdentityItems?.length"
      class="w-full rounded-[var(--base-border-radius)] p-3 mb-4"
      :class="props.allowedAccessIdentityItemsClass"
    >
      <div class="text-secondary text-xs">
        {{ t("labels.access_time") }}:
      </div>

      <li
        v-for="item in props.allowedAccessIdentityItems"
        :key="item.id"
        :data-test-category-id="item.id"
        class="text-main text-xs flex items-center my-2 gap-2"
      >
        <IconCheck :height="12" :width="12" />
        {{ item.title || "---" }}
      </li>
    </ul>

    <div class="flex items-center justify-between">
      <div class="cardSubscriptionNew__price text-main uppercase">
        {{ props.price }}
      </div>

      <div v-if="props.releaseDateTitle" class="cardSubscriptionNew__releaseDate text-secondary">
        <div class="truncate">
          {{ t("labels.release_date") }}
        </div>
        <p>{{ props.releaseDateTitle }}</p>
      </div>

      <div v-else-if="props.active" class="flex items-center text-secondary gap-1.5">
        <IconCheck :height="12" :width="12" fill="currentColor" />
        <span class="text-caption-medium">{{ t("pages.account_subscription.active_subscription") }}</span>
      </div>
      <BaseButton
        v-else
        class="font-bold text-main text-center"
        :class="!props.isRegionAvailable ? 'text-xs truncate sm:text-sm' : 'text-base'"
        name="subscribe"
        :type="props.isRegionAvailable ? 'primary' : 'secondary'"
        :disabled="props.disabled || !props.isRegionAvailable"
        @click.prevent="onClickSubscribe()"
      >
        {{ !props.isRegionAvailable ? props.regionUnavailableMessage : (props.buttonLabel || t("pages.account_subscription.button_subscribe_now")) }}
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss">
.cardSubscriptionNew {
  @apply px-4 py-5;

  background-color: var(--base-card-bgColor);
  border-radius: var(--base-border-radius);

  .cardSubscriptionNew__title {
    @apply mb-4 min-h-[1.5rem];
  }

  .cardSubscriptionNew__price {
    @apply text-xl sm:text-2xl font-bold;
  }

  .cardSubscriptionNew__description {
    @apply mb-4 min-h-[1.5rem] text-xs sm:text-base font-medium;
  }

  .cardSubscriptionNew__releaseDate {
    @apply px-[2.5rem] py-[0.5rem] text-xs rounded-md flex flex-col items-center justify-center;
    background-color: var(--base-card-bgSecondaryColor);
  }
}
</style>
